import React, { useEffect, useState } from "react";
import { Link } from "gatsby";
import { AnimatePresence, motion } from "framer-motion";
import { Disclosure } from "@headlessui/react";

import { Gradient } from "whatamesh";

import {
  ChevronDownIcon,
  ChevronUpIcon,
  PhoneIcon,
  PencilSquareIcon,
  CodeBracketIcon,
  RocketLaunchIcon,
  ComputerDesktopIcon,
} from "@heroicons/react/24/outline";

import Layout from "../components/Layout";
import Seo from "../components/Seo";

import image1 from "../images/marrapodiarchitecture.jpg";
import image3 from "../images/SEO .jpeg";
import image2 from "../images/Stables-brochure.jpg";
import image4 from "../images/Website Maintenance.jpeg";

const list = {
  visible: {
    opacity: 1,
    transition: {
      when: "beforeChildren",
      staggerChildren: 0.25,
    },
  },
  hidden: {
    opacity: 0,
    transition: {
      when: "afterChildren",
    },
  },
};

const item = {
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.75,
    },
  },
  hidden: { opacity: 0, y: 25 },
};

const faqs = [
  {
    question: "How much does a website cost?",
    answer:
      "The cost of a website can vary widely depending on a number of factors, including the size and complexity of the website, the type of design and functionality required, and any additional services such as branding or SEO. Reach out today for a free quote.",
  },
  {
    question: "How long does it take to build a website?",
    answer:
      "It is important to keep in mind that building a website is a process that involves several steps, including consultation, planning, design, development, and testing. Each of these steps can take a certain amount of time, and the overall timeline will depend on how quickly the client is able to provide content and resources and make decisions about the website.",
  },
  {
    question: "Do you offer any ongoing support or maintenance for websites?",
    answer:
      "Yes! Often times support and maintenance services can be part of a package deal, even including content updates, otherwise I also offer these services on an as-needed basis.",
  },
  {
    question: "Do you provide hosting for websites?",
    answer:
      "Yes! Your website I can arrange for your website to be hosted on Netlify which serves as the globally distributed CDN which allows for fast, low latency websites.",
  },
  {
    question: "Can you integrate e-commerce functionality into a website?",
    answer:
      "Yes, I can integrate e-commerce functionality into a website using a headless setup. A headless e-commerce setup refers to a system where the front-end of the website (the part that users see and interact with) is separate from the back-end (the part that handles the e-commerce functionality). This allows for greater flexibility, customization of the website design, and improved user experience. It also makes it easier to integrate with other systems and platforms.",
  },
  {
    question: "Can you help with search engine optimization (SEO)?",
    answer:
      "Each of my websites uses best practices to optimize on-page and technical SEO. Optimizing the content and structure of a website in order to make it more attractive to search engines. This includes things like optimizing title tags, headings, and meta descriptions. Additionally, I can assist with keyword research, that helps clients identify the most effective keywords and phrases to target.",
  },
  {
    question: "Do you offer a content management system (CMS) for websites?",
    answer:
      "I offer various content management systems to use to manage your website. Depending on the needs and goals of the client will best determine the appropriate CMS to use.",
  },
  {
    question: "Do you offer mobile-responsive design for websites?",
    answer:
      "Yes, all my websites are mobile-responsive design by default. I understand the importance of having a website that is accessible and easy to use on a variety of devices, and I strive to ensure that all of our websites are mobile responsive and accessible by default.",
  },
  {
    question: "Do you offer branding assistance for businesses?",
    answer:
      "I understand the important of branding and the role it plays in establishing a strong cohesive identity for a business. I am able to help with a variety of branding elements, including typography, color scheme creation and logo creation.",
  },
];

const services = [
  {
    name: "Custom Web Design/Development",
    desc: "I create tailored, high-performing websites that seamlessly combine stunning design with functionality to meet your business goals.",
    bullets: [
      "Fully customized to reflect your brand and vision",
      "Responsive designs for an optimal experience on all devices",
      "Built with modern, secure, and scalable technologies",
      "Optimized for speed, performance, and user engagement",
      "Seamless integration with tools and platforms you rely on",
    ],
    image: image1,
  },
  {
    name: "Brand Identity Design",
    desc: "I develop cohesive and impactful brand identities that communicate your values and make a lasting impression.",
    bullets: [
      "Custom logo design tailored to your brand",
      "Consistent color palettes and typography selection",
      "Design of business cards, letterheads, and other assets",
      "Visual guidelines to maintain brand consistency",
      "Brand storytelling through design elements",
    ],
    image: image2,
  },
  {
    name: "Search Engine Optimization (SEO)",
    desc: "I optimize your website to improve search engine visibility, driving organic traffic and helping your business stand out online.",
    bullets: [
      "In-depth keyword research tailored to your audience",
      "On-page optimization for content and meta elements",
      "Technical SEO to enhance site performance and crawlability",
      "Local SEO strategies to target nearby customers",
      "Monthly reporting and performance tracking",
    ],
    image: image3,
  },
  {
    name: "Website Maintenance and Support",
    desc: "I provide ongoing maintenance and updates to ensure your website remains secure, functional, and up-to-date at all times.",
    bullets: [
      "Regular updates to plugins, themes, and core software",
      "24/7 monitoring for security and performance issues",
      "Bug fixes and troubleshooting for a seamless experience",
      "Backup and recovery solutions to protect your data",
      "Customizable support plans based on your needs",
    ],
    image: image4,
  },
];

const timelineItems = [
  {
    title: "Consultation",
    description:
      "The first step in building a new website is to schedule a consultation. This is an opportunity for me to learn more about your business, your goals for the website, and any specific requirements or preferences you have.",
    icon: PhoneIcon,
  },
  {
    title: "Planning and Sitemap",
    description:
      "The next step is to plan out the structure and content of the site. This includes creating a sitemap, which is a visual representation of the pages and content on the website.",
    icon: PencilSquareIcon,
  },
  {
    id: 3,
    title: "Digital Mock-up Design",
    description:
      "Once the sitemap is complete, the next step is to create a digital mock-up of the website design. This is a visual representation of how the website will look, including the layout, colors, and typography. The design is sent to you where you will have the opportunity to make revisions.",
    icon: ComputerDesktopIcon,
  },
  {
    id: 4,
    title: "Development",
    description:
      "After the digital mock-up is approved, the website can be developed. This involves coding the website in Gatsby JS, a static site generator. Gatsby helps me build fast-loading, secure, and SEO-friendly websites that not only look great, but also perform well in search engines.",
    icon: CodeBracketIcon,
  },
  {
    id: 5,
    title: "Launch",
    description:
      "Once the website is developed and tested, it is ready to be launched. This includes publishing the website and making it live on the internet, as well as any necessary post-launch activities such as SEO optimization and marketing.",
    icon: RocketLaunchIcon,
  },
];

const ServicesPage = () => {
  return (
    <Layout>
      <Seo title="Custom Web Development Services" />
      <ServicesSection />
      <Process />
      <Faq />
    </Layout>
  );
};

const ServicesSection = () => {
  const [hoveredServiceIndex, setHoveredServiceIndex] = useState(0);

  useEffect(() => {
    const gradient = new Gradient();
    gradient.initGradient("#gradient-canvas-cta");
  }, []);
  return (
    <div className="overflow-hidden relative">
      <canvas
        id="gradient-canvas-cta"
        data-transition-in
        className="absolute inset-0 opacity-30 z-0"
      />

      <div className="max-w-7xl mx-auto mt-20 md:mt-32 py-36 px-4 md:py-20">
        <div className="relative mx-auto max-w-5xl">
          <p className="mb-4 text-center subtitle">What I Can Offer</p>
          <motion.h1
            initial={{ y: 10, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
            className="relative max-w-lg mx-auto text-center"
          >
            Services
          </motion.h1>
          <motion.div
            initial={{ width: 0, opacity: 0 }}
            whileInView={{ width: 100, opacity: 1 }}
            transition={{ duration: 1 }}
            viewport={{ once: true }}
            className="my-5 mx-auto h-1 w-20 rounded bg-brand"
          ></motion.div>
          <div className="w-full flex flex-col items-center mt-12">
            {/* Heading and Introductory Text */}
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold mx-auto">
                Full-Service Website Creation:{" "}
                <span className="text-brand">From Zero to Launch</span>
              </h2>
              <p className="mt-4 max-w-prose text-slate-700">
                Not sure where to start? From initial planning and design to
                development and launch, you'll work directly with me, and I'll
                provide personalized support and guidance throughout the
                process. Let me take care of the technical details so you can
                focus on growing your business.
              </p>
            </div>

            {/* Horizontal Underlined Tabs */}
            <div className="flex md:flex-row flex-col justify-center gap-8 pb-2 mb-8">
              {services.map((service, index) => (
                <button
                  key={index}
                  onClick={() => setHoveredServiceIndex(index)}
                  className={`text-base md:text-lg text-center font-medium cursor-pointer transition-all pb-1 ${
                    hoveredServiceIndex === index
                      ? "border-b-4 border-brand text-brand"
                      : "border-b-4 border-transparent text-slate-700 hover:border-slate-400"
                  }`}
                >
                  <h3>{service.name}</h3>
                </button>
              ))}
            </div>

            {/* Content Section with Animation */}
            <div className="w-full flex flex-col md:flex-row items-center gap-x-12 mt-8">
              <AnimatePresence mode="wait">
                {hoveredServiceIndex !== null && (
                  <motion.div
                    key={hoveredServiceIndex} // Unique key to trigger animation
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.4 }}
                    className="w-full flex flex-col md:flex-row items-center gap-x-12"
                  >
                    {/* Image Section */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.5 }}
                      className="flex h-full w-full md:w-1/2 justify-center"
                    >
                      <img
                        src={services[hoveredServiceIndex].image}
                        alt={services[hoveredServiceIndex].name}
                        className="w-full  rounded-xl"
                      />
                    </motion.div>

                    {/* Text Section */}
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.5 }}
                      className="flex flex-col md:w-1/2 mt-6 md:mt-0"
                    >
                      <div className="text-left">
                        <h3 className="text-2xl font-bold text-brand">
                          {services[hoveredServiceIndex].name}
                        </h3>
                        <p className="mt-4 text-slate-700 leading-loose">
                          {services[hoveredServiceIndex].desc}
                        </p>
                        <ul className="mt-4 text-slate-700 list-disc list-inside leading-loose">
                          {services[hoveredServiceIndex].bullets.map(
                            (bullet, i) => (
                              <li key={i}>{bullet}</li>
                            )
                          )}
                        </ul>
                      </div>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Process = () => {
  return (
    <div className="relative w-full mx-auto py-20 md:py-44 px-4 ">
      <div className="mx-auto max-w-5xl px-4">
        <div className="">
          <div className="flex flex-col md:flex-row gap-x-4 relative h-full">
            <motion.div
              initial="hidden"
              whileInView="visible"
              variants={list}
              viewport={{ once: true }}
              className="md:w-1/2 md:sticky md:top-1/4 md:h-screen md:overflow-y-auto mb-5"
            >
              <motion.p
                variants={item}
                className="subtitle text-center mx-auto md:text-left md:mx-0"
              >
                My Workflow
              </motion.p>
              <motion.h2
                variants={item}
                className="text-center mx-auto md:text-left md:mx-0"
              >
                The Process
              </motion.h2>
              <motion.div
                variants={item}
                className="my-5 mx-auto h-1 w-20 rounded bg-brand md:mx-0"
              ></motion.div>
              <motion.p
                variants={item}
                className="text-center mx-auto md:text-left md:mx-0"
              >
                The process for creating a website is designed to ensure that I
                deliver a high-quality product that meets the needs and goals of
                my clients. Here is a brief overview of the steps involved:
              </motion.p>
            </motion.div>

            <div className="space-y-8 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-brand/50 before:to-transparent w-full">
              {timelineItems.map((item, index) => (
                <div
                  key={index}
                  className={`relative flex items-center justify-between md:justify-normal ${
                    index % 2 === 0
                      ? "md:odd:flex-row-reverse md:ml-4"
                      : "md:even:flex-row md:mr-4"
                  } group is-active`}
                >
                  <div className="flex items-center justify-center w-12 h-12 rounded-full border border-white bg-slate-300 group-[.is-active]:bg-brand-light text-slate-500 group-[.is-active]:text-emerald-50 shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2">
                    <item.icon
                      className="h-6 w-6 text-white"
                      aria-hidden="true"
                    />
                  </div>
                  {/* Card */}
                  <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-slate-100 p-4 rounded-lg shadow-lg">
                    <div className="flex items-center justify-between space-x-2 mb-1">
                      <h4 className="">{item.title}</h4>
                    </div>
                    <div className="text-sm foont-montreal leading-loose text-gray-700">
                      {item.description}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Faq = () => {
  return (
    <div className="w-full bg-brand-bg">
      <div className="mx-auto max-w-5xl px-4 py-20">
        <div className="lg:grid lg:grid-cols-3 lg:gap-8">
          <div>
            <p className="mb-4 text-center subtitle md:text-left">
              Other Details
            </p>
            <motion.h2
              initial={{ y: 10, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 1 }}
              viewport={{ once: true }}
              className="text-slate-100 text-center md:text-left"
            >
              F.A.Q.
            </motion.h2>
            <motion.div
              initial={{ width: 0, opacity: 0 }}
              whileInView={{ width: 100, opacity: 1 }}
              transition={{ duration: 1 }}
              viewport={{ once: true }}
              className="my-5 mx-auto h-1 w-20 rounded bg-brand-light md:mx-0"
            ></motion.div>
            <p className="mt-4 text-center text-base text-slate-200 md:text-left">
              Can’t find the answer you’re looking for?{" "}
              <Link to="/contact" className="hover-fast text-brand">
                Reach out
              </Link>
            </p>
          </div>
          <div className="mt-12 lg:col-span-2 lg:mt-0">
            <motion.dl
              initial="hidden"
              whileInView="visible"
              variants={list}
              viewport={{ once: true }}
              key={item.id}
              className="space-y-6 divide-y divide-brand-light/50"
            >
              {faqs.map((faq) => (
                <Disclosure as="div" key={faq.question} className="pt-6">
                  {({ open }) => (
                    <motion.div variants={item}>
                      <dt>
                        <Disclosure.Button className="flex w-full items-start justify-between text-left text-white">
                          <span className="font-montreal-bold leading-7">
                            {faq.question}
                          </span>
                          <span className="ml-6 flex h-7 items-center">
                            {open ? (
                              <ChevronUpIcon
                                className="h-6 w-6"
                                aria-hidden="true"
                              />
                            ) : (
                              <ChevronDownIcon
                                className="h-6 w-6"
                                aria-hidden="true"
                              />
                            )}
                          </span>
                        </Disclosure.Button>
                      </dt>
                      <Disclosure.Panel as="dd" className="mt-2 pr-12">
                        <p className="text-base leading-7 text-gray-300">
                          {faq.answer}
                        </p>
                      </Disclosure.Panel>
                    </motion.div>
                  )}
                </Disclosure>
              ))}
            </motion.dl>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServicesPage;
